import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';

import { Sidebar } from './Sidebar';
import { SubscriptionBanner } from './SubscriptionBanner';
import logoMark from '@/images/Resstrologo.png';

export function Layout({ children }: { children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Закрываем drawer при смене маршрута.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Блокируем прокрутку body пока открыт мобильный drawer.
  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [navOpen]);

  return (
    <div className="h-full flex flex-col lg:flex-row">
      {/* Мобильный топбар (скрыт на lg+) */}
      <header className="lg:hidden flex items-center gap-3 px-3 h-14 bg-white border-b border-slate-100 sticky top-0 z-30">
        <button
          onClick={() => setNavOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-700"
          aria-label="Открыть меню"
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <img
            src={logoMark}
            alt="Resstro"
            className="w-7 h-7 rounded-md object-cover shrink-0"
          />
          <div className="leading-tight min-w-0">
            <div className="font-semibold text-sm truncate">Resstro SAS</div>
            <div className="text-[10px] text-slate-400">админ-панель</div>
          </div>
        </div>
      </header>

      {/* Sidebar: обычный поток на lg+, фиксированный drawer на мобильных */}
      <div
        className={[
          'fixed inset-y-0 left-0 z-50 w-[82%] max-w-xs bg-white shadow-xl transform transition-transform duration-200',
          'lg:static lg:translate-x-0 lg:w-60 lg:shadow-none lg:max-w-none',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <Sidebar onNavigate={() => setNavOpen(false)} />
      </div>

      {/* Тёмный оверлей на мобильных */}
      {navOpen && (
        <button
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
        />
      )}

      <main className="flex-1 overflow-auto bg-slate-50 min-w-0">
        <SubscriptionBanner />
        <div className="px-4 py-4 sm:px-6 sm:py-6">{children}</div>
      </main>
    </div>
  );
}
